import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreatePlatformCustomerGroupPayload,
  FinanceApiService,
  PlatformCustomerGroupMemberRecord,
  PlatformCustomerGroupRecord,
  PlatformGroupPolicyBindingRecord
} from '../finance/finance-api.service';
import { SalesApiService } from '../sales/sales-api.service';

type GroupTypeOption = { value: number; labelKey: string };
type PolicyTypeOption = { value: number; labelKey: string };
type PolicyOption = { id: string; label: string; reference: string };

@Component({
  standalone: true,
  selector: 'ain-platform-customer-group-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    RadioButtonModule,
    SelectModule,
    TableModule,
    CustomerSelectorComponent,
    TabsModule,
    InfoHintComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="isCreateMode() ? t('platform.customerGroups.form.createTitle') : t('platform.customerGroups.form.editTitle')"
      [description]="t('platform.customerGroups.detailDescription')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'partyHub.help.customerGroupDetail.title'"
        [descriptionKey]="'partyHub.help.customerGroupDetail.description'"
        [bulletKeys]="[
          'partyHub.help.customerGroupDetail.bullets.members',
          'partyHub.help.customerGroupDetail.bullets.policies',
          'partyHub.help.customerGroupDetail.bullets.status'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/platform/customer-groups']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" (click)="save()" [disabled]="saving()"></button>
        </div>

        @if (isCreateMode()) {
          <section class="card">
            <div class="grid">
              <label>
                <span>{{ t('platform.customerGroups.fields.groupCode') }}</span>
                <input pInputText [(ngModel)]="form.groupCode" />
              </label>
              <label>
                <span>{{ t('platform.customerGroups.fields.name') }}</span>
                <input pInputText [(ngModel)]="form.name" />
              </label>
              <label>
                <span>{{ t('platform.customerGroups.fields.groupType') }}</span>
                <p-select class="field-select" [options]="groupTypeOptions" optionValue="value" [(ngModel)]="form.groupType" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </section>
        } @else if (currentGroup()) {
          <p-tabs value="definition">
            <p-tablist>
              <p-tab value="definition">{{ t('partyHub.tabs.definition') }}</p-tab>
              <p-tab value="members">{{ t('partyHub.tabs.members') }}</p-tab>
              <p-tab value="policies">{{ t('partyHub.tabs.policies') }}</p-tab>
            </p-tablist>
            <p-tabpanels>
              <p-tabpanel value="definition">
                <section class="card">
                  <div class="grid">
                    <label>
                      <span>{{ t('platform.customerGroups.fields.groupCode') }}</span>
                      <input pInputText [(ngModel)]="form.groupCode" readonly />
                    </label>
                    <label>
                      <span>{{ t('platform.customerGroups.fields.name') }}</span>
                      <input pInputText [(ngModel)]="form.name" />
                    </label>
                    <label>
                      <span>{{ t('platform.customerGroups.fields.groupType') }}</span>
                      <p-select class="field-select" [options]="groupTypeOptions" optionValue="value" [(ngModel)]="form.groupType" appendTo="body">
                        <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                        <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                      </p-select>
                    </label>
                    <div class="radio-wrapper">
                      <span>{{ t('columns.status') }}</span>
                      <div class="radio-group">
                        <label class="radio-item">
                          <p-radiobutton name="groupStatus" inputId="group-status-active" [value]="true" [(ngModel)]="form.isActive" />
                          <span>{{ t('common.yes') }}</span>
                        </label>
                        <label class="radio-item">
                          <p-radiobutton name="groupStatus" inputId="group-status-inactive" [value]="false" [(ngModel)]="form.isActive" />
                          <span>{{ t('common.no') }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </section>
              </p-tabpanel>

              <p-tabpanel value="members">
          <section class="card">
            <div class="section-header">
              <h3>{{ t('platform.customerGroups.members.title') }}</h3>
            </div>
            <div class="member-row">
              <ain-customer-selector [(ngModel)]="memberCustomerId" />
              <button pButton type="button" size="small" [label]="t('platform.customerGroups.members.add')" (click)="addMember()"></button>
            </div>
            <p-table [value]="members()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('finance.customers.fields.displayName') }}</th>
                  <th>{{ t('platform.customerGroups.members.priority') }}</th>
                  <th>{{ t('platform.customerGroups.fields.actions') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>
                    <div class="member-name">{{ memberDisplayName(row) }}</div>
                    @if (row.customerCode) {
                      <div class="member-code">{{ row.customerCode }}</div>
                    }
                  </td>
                  <td>{{ row.priority }}</td>
                  <td>
                    <button pButton type="button" size="small" severity="danger" text="true" [label]="t('common.delete')" (click)="removeMember(row)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </section>
              </p-tabpanel>

              <p-tabpanel value="policies">
          <section class="card">
            <div class="section-header">
              <h3>{{ t('platform.customerGroups.policyBindings.title') }}</h3>
            </div>
            <div class="grid">
              <label>
                <span>{{ t('platform.customerGroups.policyBindings.policyType') }}</span>
                <p-select
                  class="field-select"
                  [options]="policyTypeOptions"
                  optionValue="value"
                  [(ngModel)]="bindingForm.policyType"
                  (ngModelChange)="onPolicyTypeChange($event)"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('platform.customerGroups.policyBindings.policyId') }}</span>
                <p-select
                  class="field-select"
                  [options]="policyOptions()"
                  optionValue="id"
                  optionLabel="label"
                  [(ngModel)]="bindingForm.policyId"
                  (ngModelChange)="onPolicySelected($event)"
                  [placeholder]="t('platform.customerGroups.policyBindings.selectPolicy')"
                  [disabled]="policyOptionsLoading() || policyOptions().length === 0"
                  appendTo="body">
                </p-select>
                @if (!policyOptionsLoading() && policyOptions().length === 0) {
                  <small class="field-hint">{{ t('platform.customerGroups.policyBindings.noPoliciesAvailable') }}</small>
                }
              </label>
              <label>
                <span>{{ t('platform.customerGroups.policyBindings.priority') }}</span>
                <input pInputText type="number" [(ngModel)]="bindingForm.priority" />
              </label>
            </div>
            <p class="field-hint">{{ t('platform.customerGroups.policyBindings.policyReferenceHint') }}</p>
            <button
              pButton
              type="button"
              size="small"
              [label]="t('platform.customerGroups.policyBindings.add')"
              (click)="addPolicyBinding()"
              [disabled]="!bindingForm.policyId">
            </button>
            <p-table [value]="policyBindings()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('platform.customerGroups.policyBindings.policyType') }}</th>
                  <th>{{ t('platform.customerGroups.policyBindings.policyReference') }}</th>
                  <th>{{ t('platform.customerGroups.policyBindings.priority') }}</th>
                  <th>{{ t('platform.customerGroups.fields.actions') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ policyTypeLabel(row.policyType) }}</td>
                  <td>{{ policyBindingLabel(row) }}</td>
                  <td>{{ row.priority }}</td>
                  <td>
                    <button pButton type="button" size="small" severity="danger" text="true" [label]="t('common.delete')" (click)="removePolicyBinding(row)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </section>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
        }

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; margin-bottom: 10px; }
    label { display: grid; gap: 6px; }
    .field-select { width: 100%; }
    .field-hint { margin: 0 0 10px; color: #6b7280; font-size: 0.875rem; line-height: 1.4; }
    .radio-wrapper { display: grid; gap: 6px; }
    .radio-group { display: flex; align-items: center; flex-wrap: wrap; gap: 14px; min-height: 2.5rem; }
    .radio-item { display: inline-flex; align-items: center; gap: 8px; }
    .section-header h3 { margin: 0 0 10px; font-size: 1rem; }
    .member-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; margin-bottom: 10px; align-items: end; }
    .member-name { font-weight: 600; }
    .member-code { color: #6b7280; font-size: 0.875rem; }
    .message.error { color: #b91c1c; }
  `]
})
export class PlatformCustomerGroupDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly salesApi = inject(SalesApiService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly currentGroup = signal<PlatformCustomerGroupRecord | null>(null);
  readonly members = signal<PlatformCustomerGroupMemberRecord[]>([]);
  readonly policyBindings = signal<PlatformGroupPolicyBindingRecord[]>([]);
  readonly policyOptions = signal<PolicyOption[]>([]);
  readonly policyOptionsLoading = signal(false);
  readonly policyLabelById = signal<Record<string, string>>({});
  readonly isCreateMode = signal(false);

  readonly groupTypeOptions: GroupTypeOption[] = [
    { value: 0, labelKey: 'platform.customerGroups.type.pricing' },
    { value: 1, labelKey: 'platform.customerGroups.type.credit' },
    { value: 2, labelKey: 'platform.customerGroups.type.quota' },
    { value: 3, labelKey: 'platform.customerGroups.type.accountHierarchy' },
    { value: 4, labelKey: 'platform.customerGroups.type.loyaltySegment' },
    { value: 5, labelKey: 'platform.customerGroups.type.campaignSegment' },
    { value: 6, labelKey: 'platform.customerGroups.type.complianceWatchlist' },
    { value: 7, labelKey: 'platform.customerGroups.type.other' }
  ];

  readonly policyTypeOptions: PolicyTypeOption[] = [
    { value: 0, labelKey: 'platform.customerGroups.policyType.pricing' },
    { value: 1, labelKey: 'platform.customerGroups.policyType.credit' },
    { value: 2, labelKey: 'platform.customerGroups.policyType.quota' },
    { value: 3, labelKey: 'platform.customerGroups.policyType.campaign' },
    { value: 4, labelKey: 'platform.customerGroups.policyType.approvalMatrix' },
    { value: 5, labelKey: 'platform.customerGroups.policyType.other' }
  ];

  form = { groupCode: '', name: '', groupType: 0, isActive: true };
  memberCustomerId: string | null = null;
  bindingForm = { policyType: 0, policyId: '', policyReference: '', priority: 0 };

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const groupId = params.get('groupId');
      const isCreate = !groupId || groupId === 'new';
      this.isCreateMode.set(isCreate);
      this.errorMessage.set('');

      if (isCreate) {
        this.currentGroup.set(null);
        this.members.set([]);
        this.policyBindings.set([]);
        this.loading.set(false);
        return;
      }

      this.loadGroup(groupId);
    });
  }

  save(): void {
    const groupCode = this.form.groupCode.trim();
    const name = this.form.name.trim();
    if (!groupCode || !name) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');

    if (this.isCreateMode()) {
      const payload: CreatePlatformCustomerGroupPayload = {
        groupCode,
        name,
        groupType: this.form.groupType
      };
      this.api.createPlatformCustomerGroup(payload).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/platform/customer-groups', created.id]);
        },
        error: () => {
          this.errorMessage.set(this.t('common.saveFailed'));
          this.saving.set(false);
        }
      });
      return;
    }

    const current = this.currentGroup();
    if (!current) {
      this.errorMessage.set(this.t('common.saveFailed'));
      this.saving.set(false);
      return;
    }

    this.api.updatePlatformCustomerGroup(current.id, {
      name,
      groupType: this.form.groupType,
      isActive: this.form.isActive
    }).subscribe({
      next: updated => {
        this.currentGroup.set(updated);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  addMember(): void {
    const current = this.currentGroup();
    const customerId = this.memberCustomerId?.trim();
    if (!current || !customerId) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    this.api.addPlatformCustomerGroupMember(current.id, customerId).subscribe({
      next: () => {
        this.memberCustomerId = null;
        this.loadMembers(current.id);
      },
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  removeMember(member: PlatformCustomerGroupMemberRecord): void {
    const current = this.currentGroup();
    if (!current) {
      return;
    }

    this.api.removePlatformCustomerGroupMember(current.id, member.customerId).subscribe({
      next: () => this.loadMembers(current.id),
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  onPolicyTypeChange(policyType: number): void {
    this.bindingForm.policyId = '';
    this.bindingForm.policyReference = '';
    this.loadPolicyOptions(policyType);
  }

  onPolicySelected(policyId: string | null): void {
    if (!policyId) {
      this.bindingForm.policyReference = '';
      return;
    }

    const selected = this.policyOptions().find(item => item.id === policyId);
    this.bindingForm.policyReference = selected?.reference ?? '';
  }

  addPolicyBinding(): void {
    const current = this.currentGroup();
    const policyId = this.bindingForm.policyId.trim();
    if (!current || !policyId) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    this.api.createPlatformCustomerGroupPolicyBinding(current.id, {
      policyType: this.bindingForm.policyType,
      policyId,
      policyReference: this.bindingForm.policyReference.trim() || undefined,
      priority: Number(this.bindingForm.priority) || 0
    }).subscribe({
      next: () => {
        this.bindingForm = { policyType: 0, policyId: '', policyReference: '', priority: 0 };
        this.loadPolicyOptions(0);
        this.loadPolicyBindings(current.id);
      },
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  removePolicyBinding(binding: PlatformGroupPolicyBindingRecord): void {
    const current = this.currentGroup();
    if (!current) {
      return;
    }

    this.api.deletePlatformCustomerGroupPolicyBinding(current.id, binding.id).subscribe({
      next: () => this.loadPolicyBindings(current.id),
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  memberDisplayName(member: PlatformCustomerGroupMemberRecord): string {
    return member.displayName?.trim() || member.customerCode?.trim() || member.customerId;
  }

  policyBindingLabel(binding: PlatformGroupPolicyBindingRecord): string {
    if (binding.policyReference?.trim()) {
      return binding.policyReference.trim();
    }

    return this.policyLabelById()[binding.policyId] ?? binding.policyId;
  }

  groupTypeLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const option = this.groupTypeOptions.find(item => item.value === numeric);
    return option ? this.t(option.labelKey) : `${value}`;
  }

  policyTypeLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const option = this.policyTypeOptions.find(item => item.value === numeric);
    return option ? this.t(option.labelKey) : `${value}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadGroup(groupId: string): void {
    this.loading.set(true);
    this.api.getPlatformCustomerGroup(groupId).subscribe({
      next: group => {
        this.currentGroup.set(group);
        this.form = {
          groupCode: group.groupCode,
          name: group.name,
          groupType: this.normalizeNumeric(group.groupType),
          isActive: group.isActive
        };
        this.loadMembers(group.id);
        this.loadPolicyBindings(group.id);
        this.preloadPolicyLabels();
        this.loadPolicyOptions(this.bindingForm.policyType);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  private loadMembers(groupId: string): void {
    this.api.listPlatformCustomerGroupMembers(groupId).subscribe({
      next: members => this.members.set(members),
      error: () => this.members.set([])
    });
  }

  private loadPolicyBindings(groupId: string): void {
    this.api.listPlatformCustomerGroupPolicyBindings(groupId).subscribe({
      next: bindings => this.policyBindings.set(bindings),
      error: () => this.policyBindings.set([])
    });
  }

  private loadPolicyOptions(policyType: number): void {
    this.policyOptionsLoading.set(true);
    this.policyOptions.set([]);

    if (policyType === 0) {
      this.salesApi.listPriceLists().subscribe({
        next: items => {
          const options = items.map(item => ({
            id: item.id,
            label: `${item.code} — ${item.name}`,
            reference: item.code
          }));
          this.policyOptions.set(options);
          this.mergePolicyLabels(options);
          this.policyOptionsLoading.set(false);
        },
        error: () => this.policyOptionsLoading.set(false)
      });
      return;
    }

    if (policyType === 3) {
      this.salesApi.listPromotionCampaigns().subscribe({
        next: items => {
          const options = items.map(item => ({
            id: item.id,
            label: `${item.code} — ${item.name}`,
            reference: item.code
          }));
          this.policyOptions.set(options);
          this.mergePolicyLabels(options);
          this.policyOptionsLoading.set(false);
        },
        error: () => this.policyOptionsLoading.set(false)
      });
      return;
    }

    this.policyOptionsLoading.set(false);
  }

  private preloadPolicyLabels(): void {
    forkJoin([
      this.salesApi.listPriceLists(),
      this.salesApi.listPromotionCampaigns()
    ]).subscribe({
      next: ([priceLists, campaigns]) => {
        this.mergePolicyLabels([
          ...priceLists.map(item => ({
            id: item.id,
            label: `${item.code} — ${item.name}`,
            reference: item.code
          })),
          ...campaigns.map(item => ({
            id: item.id,
            label: `${item.code} — ${item.name}`,
            reference: item.code
          }))
        ]);
      }
    });
  }

  private mergePolicyLabels(options: PolicyOption[]): void {
    if (options.length === 0) {
      return;
    }

    const next = { ...this.policyLabelById() };
    for (const option of options) {
      next[option.id] = option.label;
    }
    this.policyLabelById.set(next);
  }

  private normalizeNumeric(value: number | string): number {
    return typeof value === 'string' ? Number(value) : value;
  }
}
